import React, { useEffect, useState } from 'react';
import { App, Button, Form, Input, Select, Space, Typography } from 'antd';
import { AdHocPattern, AdHocTransferRequest, VendorItem } from '@/types';
import { vendorApi } from '@/utils/vendorApi';
import { useAuthStore } from '@/store/authStore';
import { matchesSelectedBu } from '@/utils/businessUnits';

const { Text } = Typography;

const DEFAULT_PATTERN: AdHocPattern = 'fsx_encrypt_and_move';

type VendorLoadState = 'loading' | 'loaded-with-options' | 'loaded-empty' | 'error';

function parseFilePaths(raw: unknown): string[] {
  if (typeof raw !== 'string') {
    return [];
  }
  return raw
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}

function filterVendorOption(input: string, option?: { value?: string; label?: React.ReactNode }) {
  const q = input.trim().toLowerCase();
  if (!q) {
    return true;
  }
  const vendorId = String(option?.value ?? '').toLowerCase();
  const label = String(option?.label ?? '').toLowerCase();
  return vendorId.includes(q) || label.includes(q);
}

export interface AdHocTransferFormProps {
  onSubmit: (body: AdHocTransferRequest) => void | Promise<void>;
  submitting?: boolean;
  onCancel?: () => void;
  initialVendorId?: string;
  initialFile?: string;
  singleFile?: boolean;
}

const AdHocTransferForm: React.FC<AdHocTransferFormProps> = ({
  onSubmit,
  submitting = false,
  onCancel,
  initialVendorId = '',
  initialFile = '',
  singleFile = false,
}) => {
  const { message } = App.useApp();
  const { authorization, currentBusinessUnit } = useAuthStore();
  const [form] = Form.useForm<{ vendor_id: string; files: string }>();
  const [vendors, setVendors] = useState<VendorItem[]>([]);
  const [vendorLoadState, setVendorLoadState] = useState<VendorLoadState>('loading');

  useEffect(() => {
    let cancelled = false;

    const loadVendors = async () => {
      setVendorLoadState('loading');
      try {
        const list = await vendorApi.listOperatorVendors();
        if (!cancelled) {
          setVendors(list);
          setVendorLoadState(list.length === 0 ? 'loaded-empty' : 'loaded-with-options');
        }
      } catch (error: unknown) {
        if (!cancelled) {
          setVendors([]);
          setVendorLoadState('error');
          const errorMessage = error instanceof Error ? error.message : 'Failed to load vendors';
          message.error(errorMessage);
        }
      }
    };

    void loadVendors();
    return () => {
      cancelled = true;
    };
  }, [message]);

  const handleFinish = async (values: { vendor_id: string; files: string }) => {
    const body: AdHocTransferRequest = {
      vendor_id: values.vendor_id.trim(),
      pattern: DEFAULT_PATTERN,
      files: parseFilePaths(values.files),
    };
    await onSubmit(body);
  };

  const selectedId = currentBusinessUnit?.id;
  const vendorOptions = vendors
    .filter((vendor) => !selectedId || matchesSelectedBu(vendor.bu, selectedId, authorization?.catalog))
    .map((vendor) => ({
      value: vendor.vendorId,
      label: `${vendor.vendorId} — ${vendor.bu || '—'}`,
    }));
  if (initialVendorId && !vendorOptions.some((option) => option.value === initialVendorId)) {
    vendorOptions.unshift({ value: initialVendorId, label: initialVendorId });
  }

  const showManualEntry =
    vendorLoadState === 'error' || vendorLoadState === 'loaded-empty';

  let vendorIdExtra: React.ReactNode =
    "Select a vendor in your business unit. You must be an Operator for this vendor's business unit.";
  if (vendorLoadState === 'error') {
    vendorIdExtra = (
      <Text type="secondary">
        The vendor list couldn't load. Enter the Vendor ID manually. You must be an
        Operator for this vendor's business unit.
      </Text>
    );
  } else if (vendorLoadState === 'loaded-empty') {
    vendorIdExtra = (
      <Text type="secondary">
        No vendors available for your business unit. Enter the Vendor ID manually.
      </Text>
    );
  }

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleFinish}
      initialValues={{ vendor_id: initialVendorId, files: initialFile }}
    >
      <Form.Item
        name="vendor_id"
        label="Vendor ID"
        extra={vendorIdExtra}
        rules={[
          { required: true, whitespace: true, message: 'Vendor ID is required' },
        ]}
      >
        {showManualEntry ? (
          <Input placeholder="TESTVENDOR" disabled={submitting} />
        ) : (
          <Select
            showSearch
            placeholder="Select a vendor"
            loading={vendorLoadState === 'loading'}
            disabled={submitting}
            options={vendorOptions}
            filterOption={filterVendorOption}
          />
        )}
      </Form.Item>

      <Form.Item
        label="Transfer Pattern"
        extra="Only fsx_encrypt_and_move is routed today."
      >
        <Input value={DEFAULT_PATTERN} disabled />
      </Form.Item>

      <Form.Item
        name="files"
        label="File Paths"
        extra={
          singleFile ? (
            <Text type="secondary">
              Resubmit applies to this one file. Correct the path if needed. The file
              must already be on the shared drive.
            </Text>
          ) : (
            <Text type="secondary">
              Enter one file location per line, exactly as it appears on the shared drive
              (for example: \\server\share\OUTGOING\file.csv). These are files that are
              already on the shared drive — you are not uploading them here.
            </Text>
          )
        }
        rules={[
          {
            validator: async (_, value) => {
              const paths = parseFilePaths(value);
              if (paths.length === 0) {
                return Promise.reject(new Error(
                  singleFile ? 'Enter the FSx file path' : 'Enter at least one FSx file path'
                ));
              }
              if (singleFile && paths.length > 1) {
                return Promise.reject(new Error('Only one file can be resubmitted at a time.'));
              }
            },
          },
        ]}
      >
        {singleFile ? (
          <Input
            placeholder={'\\\\fsx.example.org\\share\\OUTGOING\\VENDOR_orders.csv'}
            disabled={submitting}
          />
        ) : (
          <Input.TextArea
            rows={6}
            placeholder={'\\\\fsx.example.org\\share\\OUTGOING\\VENDOR_orders.csv'}
            disabled={submitting}
          />
        )}
      </Form.Item>

      <Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" loading={submitting} disabled={submitting}>
            {singleFile ? 'Resubmit' : 'Start transfer'}
          </Button>
          {onCancel && (
            <Button onClick={onCancel} disabled={submitting}>
              Cancel
            </Button>
          )}
        </Space>
      </Form.Item>
    </Form>
  );
};

export default AdHocTransferForm;
